import type { ReactNode } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'

import { useFeatures, useGetConfig } from '@jotta/grpc-connect-client/config'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import SvgAndroid from '@jotta/ui/SvgAndroid'
import SvgApple from '@jotta/ui/SvgApple'
import SvgLinux from '@jotta/ui/SvgLinux'
import SvgWindows from '@jotta/ui/SvgWindows'

import { PlainBrandIcon } from '../../Icons/BrandIcon/BrandIcon'

export interface DownloadSectionItem {
  icon: BrandThemeIcon
  iconButton?: {
    label: string
    link: string
  }
  downloadLink: string
}
export interface DownloadSectionProps {
  sectionItems: DownloadSectionItem[]
  headerText: string
  subHeaderText: string
  footer?: ReactNode
}

export function DownloadSection({
  sectionItems,
  headerText,
  subHeaderText,
  footer,
}: DownloadSectionProps) {
  return (
    <div className="flex flex-col items-center gap-4">
      <header className="title-md">{headerText}</header>
      <p className="pb-4 opacity-80">{subHeaderText}</p>
      <div className="grid grid-flow-col gap-10">
        {sectionItems.map(({ iconButton, downloadLink, icon }, index) => (
          <div key={`${icon}-${index}`} className="flex flex-col gap-4">
            <a className="mx-auto" href={downloadLink}>
              <PlainBrandIcon icon={icon} height={48} width={48} />
            </a>
            {iconButton && (
              <a href={iconButton.link} className="btn btn-outline">
                {iconButton.label}
              </a>
            )}
          </div>
        ))}
      </div>
      {footer}
    </div>
  )
}

export function DownloadPage() {
  const { t } = useLingui()
  const features = useFeatures()
  const {
    brandName,
    downloadMacLink = '',
    downloadWinLink = '',
    downloadIosLink = '',
    downloadAndroidLink = '',
    cliInstallationInstructionsLink = '',
  } = useGetConfig().data?.brandingInfo || {}

  const hasMobileLinks = !!downloadIosLink && !!downloadAndroidLink
  const isMobile = /iPad|iPhone|iPod|android/i.test(navigator.userAgent)
  const mobileSection = hasMobileLinks && (
    <DownloadSection
      headerText={t`${brandName} for mobile`}
      subHeaderText={t`Back up your photos and access all your files on the go.`}
      sectionItems={[
        {
          icon: SvgApple,
          downloadLink: downloadIosLink,
          iconButton: {
            label: 'App Store',
            link: downloadIosLink,
          },
        },
        {
          icon: SvgAndroid,
          downloadLink: downloadAndroidLink,
          iconButton: {
            label: 'Google play',
            link: downloadAndroidLink,
          },
        },
      ]}
    />
  )

  return (
    <div className="flex flex-col gap-16 py-12 text-center md:py-16">
      <header className="title-xl">
        <Trans>Download our apps</Trans>
      </header>

      {isMobile && mobileSection}

      <DownloadSection
        headerText={t`${brandName} for computers`}
        subHeaderText={t`Back up files, and synchronize folders across computers.`}
        sectionItems={[
          {
            icon: SvgApple,
            downloadLink: downloadMacLink,
            iconButton: {
              label: 'MacOS',
              link: downloadMacLink,
            },
          },
          {
            icon: SvgWindows,
            downloadLink: downloadWinLink,
            iconButton: {
              label: 'Windows',
              link: downloadWinLink,
            },
          },
        ]}
      />

      {!isMobile && mobileSection}

      {features.cli && cliInstallationInstructionsLink && (
        <DownloadSection
          headerText={t`${brandName} Command-Line Tool`}
          subHeaderText={t`Automatic folder backup of any system.`}
          sectionItems={[
            {
              icon: SvgApple,
              downloadLink: cliInstallationInstructionsLink,
            },
            {
              icon: SvgWindows,
              downloadLink: cliInstallationInstructionsLink,
            },
            {
              icon: SvgLinux,
              downloadLink: cliInstallationInstructionsLink,
            },
          ]}
          footer={
            <a
              className="btn btn-outline"
              href={cliInstallationInstructionsLink}
            >
              <Trans>Install instructions</Trans>
            </a>
          }
        />
      )}
    </div>
  )
}
